import { useState, useCallback, useMemo } from "react";
import {
  ChevronDown,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Target,
  BookOpen,
  CheckSquare,
  Square,
  Edit3,
  X,
  Play,
  XCircle,
  Calendar,
  Sparkles,
  Star,
  ArrowRight,
  CheckCircle2,
  Wand2,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation } from "@apollo/client/react";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { SubjectIcon } from "@/components/subject-icon";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import {
  APPLY_SKILL_SETUP,
  BULK_UPDATE_PROFICIENCIES,
  SET_SUBJECT_PIN,
  SET_SUBJECT_STRANDS,
  UPDATE_PROFICIENCY,
} from "@/graphql/mutations/students";
import { GET_STUDENT_SKILLS } from "@/graphql/queries/students";
import { gradeLabel, skillsLabel } from "@/lib/students";
import {
  SkillStatusControl,
  AssumedMarkBadge,
  ManualMarkBadge,
  STATUS_META,
  type SkillStatus,
} from "@/components/skills/skill-status";
import {
  InactiveSubjectsStrip,
  SubjectVisibilityButton,
  type LearnerSubject,
  type PinState,
  type SubjectStrand,
} from "@/components/skills/subject-visibility";
import { SkillSetupWizard } from "@/components/skills/setup-wizard";

type HistorySession = {
  id: string;
  status: string;
  completedAt: string | null;
  createdAt: string;
  subjectKey: string | null;
  correctCount: number;
  totalCount: number;
  pointsEarned: number;
  subject: { id: string; name: string; icon: string } | null;
};

type HistoryEntry = {
  id: string;
  change: number;
  reason: string;
  createdAt: string;
  session: HistorySession | null;
};

type Skill = {
  id: string;
  name: string;
  difficulty: number;
  grade: number;
  prerequisiteNames: string[];
  subjectId: string;
  strand: string | null;
};

/** Everything the server sends for every skill — enough to draw a one-line row. */
type ProficiencyBase = {
  id: string;
  skillKey: string;
  proficiency: number;
  totalAttempts: number;
  correctAttempts: number;
  isUnlocked: boolean;
  isPriority: boolean;
  setupStatus: SkillStatus;
  manuallySet: boolean;
  assumed: boolean;
  skill: Skill | null;
};

/** The rest of a card, fetched only for the rows that get one — see
    `studentSkills.liveProficiencies`. Absent, not zero, on a row without a
    card: nothing has been counted for it, and a zero would say it had. */
export type ProficiencyDetail = {
  id: string;
  correctExerciseCount: number;
  incorrectExerciseCount: number;
  conceptsCovered: boolean;
  conceptProgress: ConceptProgress[];
  history: HistoryEntry[];
};

type Proficiency = ProficiencyBase & Partial<Omit<ProficiencyDetail, "id">>;

/** One thing the skill teaches, and how this learner stands on it. */
type ConceptProgress = {
  key: string;
  name: string;
  proficiency: number;
  totalAttempts: number;
  questionCount: number;
  mastered: boolean;
  untouched: boolean;
};

type PriorityStep = {
  skillKey: string;
  skillName: string | null;
  grade: number | null;
  proficiency: number;
  isUnlocked: boolean;
  isTarget: boolean;
};

type PriorityPlan = {
  targetSkillKey: string;
  targetSkillName: string | null;
  subjectKey: string | null;
  steps: PriorityStep[];
};

type Subject = {
  id: string;
  name: string;
  proficiencies: Proficiency[];
};

type UnfinishedSession = {
  id: string;
  subjectKey: string;
  status: string;
  updatedAt: string;
};

type StudentSummary = {
  id: string;
  name: string;
  age: number;
  grade: number | null;
  gradeKnown: boolean;
  skillSetupDone: boolean;
};

type SkillsManagerProps = {
  student: StudentSummary;
  subjects: Subject[];
  learnerSubjects: LearnerSubject[];
  unfinishedSessions: UnfinishedSession[];
  priorityPlans: PriorityPlan[];
  liveProficiencies: ProficiencyDetail[];
  studentId: string;
};

const SUBJECT_ICONS: Record<string, string> = {
  math: "Calculator",
  lithuanian: "BookOpen",
  english: "Globe",
  physics: "Atom",
  chemistry: "FlaskConical",
  geography: "Globe",
};

// Below this a skill that has actually been practised counts as going badly —
// the one number on this page worth acting on today.
const STRUGGLING_BELOW = 50;

function getProficiencyLevel(value: number): {
  label: string;
  color: string;
  bgColor: string;
} {
  if (value >= 90) return { label: "Meistriškas", color: "text-emerald-700 dark:text-emerald-400", bgColor: "bg-emerald-500" };
  if (value >= 70) return { label: "Geras", color: "text-blue-700 dark:text-blue-400", bgColor: "bg-blue-500" };
  if (value >= 50) return { label: "Vidutinis", color: "text-amber-700 dark:text-amber-400", bgColor: "bg-amber-500" };
  if (value >= 30) return { label: "Pradinis", color: "text-orange-700 dark:text-orange-400", bgColor: "bg-orange-500" };
  return { label: "Naujas", color: "text-gray-500 dark:text-gray-400", bgColor: "bg-gray-400" };
}

function formatRelativeShort(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "ką tik";
  if (diffMin < 60) return `prieš ${diffMin} min.`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `prieš ${diffHr} val.`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `prieš ${diffDay} d.`;
  return d.toLocaleDateString("lt-LT", { year: "numeric", month: "short", day: "numeric" });
}

/** A skill worth a full card: one the learner has met, or one being aimed at. */
function isLive(prof: Proficiency): boolean {
  return prof.totalAttempts > 0 || prof.isPriority;
}

/**
 * Only the skills in strands this learner studies. A switched-off strand is not
 * planned, so its skills are not something to set up or watch here either —
 * the strand row above the grades says how many were left out.
 */
function studiedProficiencies<T extends ProficiencyBase>(
  proficiencies: T[],
  strands: SubjectStrand[] | undefined
): T[] {
  const off = new Set((strands ?? []).filter((s) => !s.enabled).map((s) => s.key));
  if (off.size === 0) return proficiencies;
  return proficiencies.filter((p) => !p.skill?.strand || !off.has(p.skill.strand));
}

/**
 * Which parts of the subject this learner's sessions draw from. Each chip is a
 * switch; the last one on cannot be turned off, because a subject with nothing
 * in it is a hidden subject and the eye button already does that.
 */
function StrandChooser({
  strands,
  onChange,
  disabled,
}: {
  strands: SubjectStrand[];
  onChange: (keys: string[]) => void;
  disabled: boolean;
}) {
  const enabledKeys = strands.filter((s) => s.enabled).map((s) => s.key);
  const offCount = strands.filter((s) => !s.enabled).reduce((n, s) => n + s.skillCount, 0);

  const toggle = (key: string) => {
    const next = enabledKeys.includes(key)
      ? enabledKeys.filter((k) => k !== key)
      : [...enabledKeys, key];
    if (next.length === 0) return;
    onChange(next);
  };

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <span className="text-xs text-muted-foreground">Mokosi:</span>
      {strands.map((strand) => {
        const lastOn = strand.enabled && enabledKeys.length === 1;
        return (
          <Tooltip key={strand.key}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(strand.key);
                }}
                disabled={disabled || lastOn}
                aria-pressed={strand.enabled}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed",
                  strand.enabled
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-dashed text-muted-foreground line-through hover:bg-muted"
                )}
              >
                {strand.enabled ? (
                  <CheckSquare className="h-3.5 w-3.5" />
                ) : (
                  <Square className="h-3.5 w-3.5" />
                )}
                {strand.name}
              </button>
            </TooltipTrigger>
            <TooltipContent>
              {strand.description} · {skillsLabel(strand.skillCount)}
              {lastOn && " — bent viena dalis turi likti"}
            </TooltipContent>
          </Tooltip>
        );
      })}
      {offCount > 0 && (
        <span className="text-xs text-muted-foreground">
          {skillsLabel(offCount)} neįtraukta į sesijas
        </span>
      )}
    </div>
  );
}

function HistoryRow({ entry }: { entry: HistoryEntry }) {
  const session = entry.session;
  const positive = entry.change > 0;
  const negative = entry.change < 0;
  const accuracy =
    session && session.totalCount > 0
      ? Math.round((session.correctCount / session.totalCount) * 100)
      : null;
  const dateIso = session?.completedAt || session?.createdAt || entry.createdAt;
  const isManual = !session && /rankin/i.test(entry.reason);
  const isInitial = !session && /pradi|inicia|nustatyt/i.test(entry.reason);

  const changeBadge = (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-mono font-semibold tabular-nums shrink-0",
        positive && "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400",
        negative && "bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400",
        !positive && !negative && "bg-muted text-muted-foreground"
      )}
    >
      {positive ? (
        <TrendingUp className="h-3 w-3" />
      ) : negative ? (
        <TrendingDown className="h-3 w-3" />
      ) : null}
      {positive ? "+" : ""}
      {entry.change.toFixed(1)}%
    </span>
  );

  if (session) {
    const subjectName = session.subject?.name;
    return (
      <Link
        to={`/sessions/${session.id}`}
        className={cn(
          "group flex items-center gap-2 text-xs rounded-md px-2 py-1.5 -mx-1 transition-colors",
          "hover:bg-muted/60",
          positive && "hover:bg-emerald-50/60 dark:hover:bg-emerald-950/20",
          negative && "hover:bg-red-50/60 dark:hover:bg-red-950/20"
        )}
        title={dateIso ? new Date(dateIso).toLocaleString("lt-LT") : undefined}
      >
        {changeBadge}
        <div className="flex-1 min-w-0 flex items-center gap-1.5 flex-wrap">
          {session.subject?.icon && (
            <SubjectIcon
              icon={session.subject.icon}
              className="h-3 w-3 text-muted-foreground shrink-0"
            />
          )}
          {subjectName && (
            <span className="font-medium text-foreground/80 truncate">{subjectName}</span>
          )}
          {accuracy !== null && (
            <Badge variant="outline" className="h-4 px-1 text-[10px] font-medium">
              {session.correctCount}/{session.totalCount} · {accuracy}%
            </Badge>
          )}
          {session.status !== "COMPLETED" && (
            <Badge variant="secondary" className="h-4 px-1 text-[10px] font-medium">
              {session.status === "GRADED" ? "Įvertinta" : session.status}
            </Badge>
          )}
        </div>
        <span className="text-muted-foreground shrink-0 inline-flex items-center gap-0.5 group-hover:text-foreground/70 transition-colors">
          <Calendar className="h-3 w-3" />
          {dateIso ? formatRelativeShort(dateIso) : ""}
        </span>
      </Link>
    );
  }

  return (
    <div
      className="flex items-center gap-2 text-xs px-2 py-1.5 -mx-1"
      title={dateIso ? new Date(dateIso).toLocaleString("lt-LT") : undefined}
    >
      {changeBadge}
      <span className="flex-1 min-w-0 flex items-center gap-1.5 text-muted-foreground truncate">
        {isManual && <Edit3 className="h-3 w-3 shrink-0" />}
        {isInitial && <Sparkles className="h-3 w-3 shrink-0" />}
        <span className="truncate">{entry.reason}</span>
      </span>
      {dateIso && (
        <span className="text-muted-foreground/70 shrink-0">{formatRelativeShort(dateIso)}</span>
      )}
    </div>
  );
}

function PriorityStar({
  proficiency,
  onToggle,
  disabled,
}: {
  proficiency: Proficiency;
  onToggle: (proficiency: Proficiency) => void;
  disabled: boolean;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => onToggle(proficiency)}
          disabled={disabled}
          aria-pressed={proficiency.isPriority}
          aria-label={
            proficiency.isPriority ? "Pašalinti iš prioritetų" : "Pažymėti kaip prioritetą"
          }
          className={cn(
            "rounded-md p-1 transition-colors disabled:opacity-50",
            proficiency.isPriority
              ? "text-amber-500 hover:text-amber-600"
              : "text-muted-foreground/40 hover:text-amber-500"
          )}
        >
          <Star className={cn("h-4 w-4", proficiency.isPriority && "fill-current")} />
        </button>
      </TooltipTrigger>
      <TooltipContent>
        {proficiency.isPriority
          ? "Prioritetas — sesijos eina link šio įgūdžio"
          : "Pažymėti kaip tikslą, link kurio eiti"}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * A skill nobody has met yet: one line, not a card.
 *
 * Nine out of ten skills on this page are all zeros, and a card full of zeros
 * costs the same two hundred pixels as one describing real work. The whole
 * subject used to be fifty screens of them.
 */
function SkillRow({
  proficiency,
  isSelected,
  onToggleSelect,
  showCheckbox,
  onStatusChange,
  onTogglePriority,
  busy,
}: {
  proficiency: Proficiency;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  showCheckbox: boolean;
  onStatusChange: (proficiency: Proficiency, status: SkillStatus) => void;
  onTogglePriority: (proficiency: Proficiency) => void;
  busy: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-md px-2 py-1 transition-colors hover:bg-muted/50",
        isSelected && "bg-primary/5 ring-1 ring-primary/40"
      )}
    >
      {showCheckbox && (
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggleSelect(proficiency.id)}
          className="shrink-0"
        />
      )}

      <span className="min-w-0 flex-1 truncate text-sm">
        {proficiency.skill?.name ?? proficiency.skillKey}
      </span>

      {proficiency.manuallySet && <ManualMarkBadge />}
      {proficiency.assumed && <AssumedMarkBadge />}

      {/* An assumed percentage is not a result. Printed as a bare number it
          reads exactly like one the learner earned, which is the thing the
          badge exists to prevent. */}
      {proficiency.proficiency > 0 && !proficiency.manuallySet && !proficiency.assumed && (
        <span className="w-9 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
          {Math.round(proficiency.proficiency)}%
        </span>
      )}

      <PriorityStar
        proficiency={proficiency}
        onToggle={onTogglePriority}
        disabled={busy}
      />

      <SkillStatusControl
        value={proficiency.setupStatus}
        onChange={(next) => onStatusChange(proficiency, next)}
        disabled={busy}
        compact
      />
    </div>
  );
}

/** A skill with real history behind it. */
function SkillCard({
  proficiency,
  isSelected,
  onToggleSelect,
  showCheckbox,
  studentId,
  onStatusChange,
  onTogglePriority,
  busy,
}: {
  proficiency: Proficiency;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  showCheckbox: boolean;
  studentId: string;
  onStatusChange: (proficiency: Proficiency, status: SkillStatus) => void;
  onTogglePriority: (proficiency: Proficiency) => void;
  busy: boolean;
}) {
  const [showHistory, setShowHistory] = useState(false);
  const level = getProficiencyLevel(proficiency.proficiency);
  const skill = proficiency.skill;
  const accuracy =
    proficiency.totalAttempts > 0
      ? Math.round(
          (proficiency.correctAttempts / proficiency.totalAttempts) * 100
        )
      : 0;

  return (
    <div
      className={cn(
        "rounded-lg border p-4 transition-all",
        proficiency.isUnlocked
          ? "bg-card hover:shadow-sm"
          : "bg-muted/40 opacity-70",
        // A flagged skill stays legible even while locked — it is the thing
        // the learner is heading for, so it should not read as greyed out.
        proficiency.isPriority && "border-amber-300 dark:border-amber-700 opacity-100",
        isSelected && "ring-2 ring-primary"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {showCheckbox && (
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onToggleSelect(proficiency.id)}
              className="shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium leading-tight truncate">
              {skill?.name ?? proficiency.skillKey}
            </h4>
            <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
              {skill && <span>{gradeLabel(skill.grade)}</span>}
              {proficiency.manuallySet && <ManualMarkBadge />}
              {proficiency.assumed && <AssumedMarkBadge />}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <PriorityStar
            proficiency={proficiency}
            onToggle={onTogglePriority}
            disabled={busy}
          />
          <Badge
            variant="outline"
            className={cn("shrink-0 text-xs font-medium", level.color)}
          >
            {level.label}
          </Badge>
        </div>
      </div>

      <div className="mt-3">
        <SkillStatusControl
          value={proficiency.setupStatus}
          onChange={(next) => onStatusChange(proficiency, next)}
          disabled={busy}
        />
      </div>

      {/* Proficiency bar */}
      <div className="mt-3 space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Lygis</span>
          <span className="font-medium">
            {Math.round(proficiency.proficiency)}%
          </span>
        </div>
        <div className="relative">
          <Progress
            value={proficiency.proficiency}
            className={cn("h-2", `[&>[data-slot=progress-indicator]]:${level.bgColor}`)}
          />
        </div>
      </div>

      {/* Stats */}
      <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex items-center gap-1 cursor-default">
              <Target className="h-3 w-3" />
              {accuracy}% tikslumas
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {proficiency.correctAttempts} / {proficiency.totalAttempts} teisingi
          </TooltipContent>
        </Tooltip>
        <span className="flex items-center gap-1">
          <BookOpen className="h-3 w-3" />
          {proficiency.totalAttempts} bandymų
        </span>
        {(proficiency.incorrectExerciseCount ?? 0) > 0 && (
          <Link
            to={`/students/${studentId}/incorrect?skill=${encodeURIComponent(proficiency.skillKey)}`}
            className="flex items-center gap-1 text-red-600 dark:text-red-400 hover:underline"
          >
            <XCircle className="h-3 w-3" />
            {proficiency.incorrectExerciseCount} klaidingai
          </Link>
        )}
      </div>

      {/* What the skill is made of. The overall percentage can sit at 80% with
          one part never met — and a grown-up needs to see which one. */}
      {(proficiency.conceptProgress?.length ?? 0) > 0 && (
        <div className="mt-3 space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Temos dalys</span>
            <span
              className={cn(
                "font-medium",
                proficiency.conceptsCovered
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-muted-foreground"
              )}
            >
              {proficiency.conceptProgress!.filter((c) => c.mastered).length} /{" "}
              {proficiency.conceptProgress!.length}
            </span>
          </div>
          {proficiency.conceptProgress!.map((concept) => (
            <div key={concept.key} className="flex items-center gap-2 text-xs">
              <span
                className={cn(
                  "flex-1 truncate",
                  concept.mastered
                    ? "text-muted-foreground"
                    : concept.untouched
                      ? "text-amber-600 dark:text-amber-400"
                      : "text-foreground"
                )}
                title={`${concept.questionCount} užduotys šiai daliai`}
              >
                {concept.name}
              </span>
              <Progress value={concept.proficiency} className="h-1 w-20 shrink-0" />
              <span className="w-9 text-right tabular-nums text-muted-foreground">
                {concept.untouched ? "—" : `${Math.round(concept.proficiency)}%`}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Prerequisites only matter while the door is shut. */}
      {!proficiency.isUnlocked && skill?.prerequisiteNames && skill.prerequisiteNames.length > 0 && (
        <div className="mt-2">
          <p className="text-xs text-muted-foreground">
            Reikalavimai:{" "}
            {skill.prerequisiteNames.map((name, i) => (
              <span key={i}>
                {i > 0 && ", "}
                <span className="font-medium">{name}</span>
              </span>
            ))}
          </p>
        </div>
      )}

      {/* History toggle */}
      {(proficiency.history?.length ?? 0) > 0 && (
        <div className="mt-3 border-t pt-2">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {showHistory ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
            Istorija ({proficiency.history!.length})
          </button>

          {showHistory && (
            <div className="mt-2 space-y-1.5 max-h-72 overflow-y-auto">
              {proficiency.history!.slice(0, 20).map((entry) => (
                <HistoryRow key={entry.id} entry={entry} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * One grade's worth of skills inside a subject.
 *
 * Grouped and folded because a subject spans twelve years of schooling and a
 * child is in one of them. The grades around theirs open on their own; the rest
 * say how they stand in one line and stay out of the way.
 */
function GradeGroup({
  grade,
  proficiencies,
  defaultOpen,
  children,
}: {
  grade: number;
  proficiencies: Proficiency[];
  defaultOpen: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const known = proficiencies.filter((p) => p.setupStatus === "known").length;
  const live = proficiencies.filter(isLive).length;

  return (
    <div className="rounded-lg border">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-muted/40"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <span className="text-sm font-medium">{gradeLabel(grade)}</span>
        <span className="text-xs text-muted-foreground">
          {skillsLabel(proficiencies.length)}
        </span>
        <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          {live > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
              {live} dirbama
            </Badge>
          )}
          <span className={cn(known === proficiencies.length && "text-emerald-600 dark:text-emerald-400")}>
            {known}/{proficiencies.length} moka
          </span>
        </div>
      </button>

      {open && <div className="space-y-1 border-t px-2 py-2">{children}</div>}
    </div>
  );
}

function SubjectSection({
  subject,
  learnerSubject,
  learnerGrade,
  selectedIds,
  onToggleSelect,
  showCheckboxes,
  onSelectGrade,
  editMode,
  onEnterEditMode,
  onExitEditMode,
  onBulkStatus,
  onBulkSetPriority,
  onStatusChange,
  onTogglePriority,
  busy,
  showProfInput,
  onToggleProfInput,
  bulkProficiency,
  onBulkProficiencyChange,
  onBulkSetProficiency,
  isBulkUpdating,
  totalSelectedCount,
  gradeFilter,
  onGradeFilterChange,
  onPin,
  onSetStrands,
  studentId,
}: {
  subject: Subject;
  learnerSubject: LearnerSubject | undefined;
  learnerGrade: number | null;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  showCheckboxes: boolean;
  onSelectGrade: (subjectId: string, grade: number) => void;
  editMode: boolean;
  onEnterEditMode: () => void;
  onExitEditMode: () => void;
  onBulkStatus: (status: SkillStatus) => void;
  onBulkSetPriority: (isPriority: boolean) => void;
  onStatusChange: (proficiency: Proficiency, status: SkillStatus) => void;
  onTogglePriority: (proficiency: Proficiency) => void;
  busy: boolean;
  showProfInput: boolean;
  onToggleProfInput: () => void;
  bulkProficiency: string;
  onBulkProficiencyChange: (val: string) => void;
  onBulkSetProficiency: () => void;
  isBulkUpdating: boolean;
  totalSelectedCount: number;
  gradeFilter: number | null;
  onGradeFilterChange: (grade: number | null) => void;
  onPin: (subjectId: string, state: PinState) => void;
  onSetStrands: (subjectId: string, strands: string[]) => void;
  studentId: string;
}) {
  const [isExpanded, setIsExpanded] = useState(true);

  const iconName = SUBJECT_ICONS[subject.id] ?? "BookOpen";

  const knownCount = subject.proficiencies.filter((p) => p.setupStatus === "known").length;
  const liveCount = subject.proficiencies.filter(isLive).length;
  const totalCount = subject.proficiencies.length;

  // The average over every skill in the subject is dominated by the hundreds
  // nobody has opened, so it reports ~20% for a learner doing well. Averaging
  // what they have actually met answers the question being asked.
  const met = subject.proficiencies.filter((p) => p.proficiency > 0);
  const avgProficiency =
    met.length > 0 ? met.reduce((sum, p) => sum + p.proficiency, 0) / met.length : 0;

  const grades = [
    ...new Set(
      subject.proficiencies
        .map((p) => p.skill?.grade)
        .filter((g): g is number => g != null)
    ),
  ].sort((a, b) => a - b);

  const filteredProficiencies =
    gradeFilter != null
      ? subject.proficiencies.filter((p) => p.skill?.grade === gradeFilter)
      : subject.proficiencies;

  const byGrade = useMemo(() => {
    const groups = new Map<number, Proficiency[]>();
    filteredProficiencies.forEach((prof) => {
      const grade = prof.skill?.grade ?? 0;
      const list = groups.get(grade) ?? [];
      list.push(prof);
      groups.set(grade, list);
    });
    // Within a grade: what is being worked on, then what is furthest along.
    groups.forEach((list) =>
      list.sort((a, b) => {
        if (isLive(a) !== isLive(b)) return isLive(a) ? -1 : 1;
        return b.proficiency - a.proficiency;
      })
    );
    return [...groups.entries()].sort((a, b) => a[0] - b[0]);
  }, [filteredProficiencies]);

  const selectedInGrade =
    gradeFilter != null
      ? subject.proficiencies.filter(
          (p) => p.skill?.grade === gradeFilter && selectedIds.has(p.id)
        ).length
      : 0;

  // A grade opens on its own when it holds work in progress, or when it is the
  // one the learner is in or just finished. Everything else is one folded line.
  const shouldOpenGrade = (grade: number, list: Proficiency[]) => {
    if (gradeFilter != null) return true;
    if (list.some(isLive)) return true;
    if (learnerGrade == null) return false;
    return grade >= learnerGrade - 1 && grade <= learnerGrade;
  };

  return (
    <Card>
      <CardHeader>
        <div
          className="flex items-center justify-between cursor-pointer select-none"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-3">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            <SubjectIcon icon={iconName} className="h-5 w-5" />
            <CardTitle className="text-lg">{subject.name}</CardTitle>
            {learnerSubject?.pinned && (
              <Badge variant="outline" className="text-[10px] font-normal">
                prisegta
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="secondary" className="text-xs">
              {knownCount}/{totalCount} moka
            </Badge>
            {liveCount > 0 && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>{Math.round(avgProficiency)}%</span>
                <Progress value={avgProficiency} className="h-2 w-20" />
              </div>
            )}
            {isExpanded && !editMode && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onEnterEditMode();
                }}
                className="text-muted-foreground"
              >
                <Edit3 className="mr-1.5 h-3.5 w-3.5" />
                Žymėti daug
              </Button>
            )}
            {learnerSubject && (
              <SubjectVisibilityButton
                subject={learnerSubject}
                onPin={onPin}
                disabled={busy}
              />
            )}
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0">
          {learnerSubject?.strands && learnerSubject.strands.length > 0 && (
            <StrandChooser
              strands={learnerSubject.strands}
              onChange={(keys) => onSetStrands(subject.id, keys)}
              disabled={busy}
            />
          )}

          {/* Grade filter */}
          {grades.length > 1 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-muted-foreground">Klasė:</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onGradeFilterChange(null);
                  }}
                  className={cn(
                    "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                    gradeFilter === null
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  )}
                >
                  Visos
                </button>
                {grades.map((grade) => (
                  <button
                    key={grade}
                    onClick={(e) => {
                      e.stopPropagation();
                      onGradeFilterChange(gradeFilter === grade ? null : grade);
                    }}
                    className={cn(
                      "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                      gradeFilter === grade
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:bg-muted/80",
                      grade === learnerGrade && gradeFilter !== grade && "ring-1 ring-primary/40"
                    )}
                  >
                    {grade} kl.
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Edit toolbar */}
          {editMode && (
            <div className="mb-4 rounded-lg border bg-muted/30 p-3 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                {gradeFilter != null && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectGrade(subject.id, gradeFilter);
                    }}
                  >
                    <CheckSquare className="mr-2 h-3.5 w-3.5" />
                    Pažymėti {gradeFilter} kl.
                    {selectedInGrade > 0 && (
                      <Badge variant="secondary" className="ml-2">
                        {selectedInGrade}
                      </Badge>
                    )}
                  </Button>
                )}

                {totalSelectedCount > 0 && (
                  <>
                    <Badge variant="secondary" className="text-xs">
                      Pažymėta: {totalSelectedCount}
                    </Badge>
                    {(["known", "learning", "not_yet"] as SkillStatus[]).map((status) => {
                      const meta = STATUS_META[status];
                      const Icon = meta.icon;
                      return (
                        <Button
                          key={status}
                          variant="outline"
                          size="sm"
                          onClick={() => onBulkStatus(status)}
                          disabled={isBulkUpdating}
                        >
                          <Icon className="mr-1.5 h-3.5 w-3.5" />
                          {meta.label}
                        </Button>
                      );
                    })}
                    <Button variant="outline" size="sm" onClick={onToggleProfInput}>
                      <Edit3 className="mr-1.5 h-3.5 w-3.5" />
                      Tikslus lygis
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onBulkSetPriority(true)}
                      disabled={isBulkUpdating}
                    >
                      <Star className="mr-1.5 h-3.5 w-3.5" />
                      Prioritetas
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onBulkSetPriority(false)}
                      disabled={isBulkUpdating}
                    >
                      <X className="mr-1.5 h-3.5 w-3.5" />
                      Nebe prioritetas
                    </Button>
                  </>
                )}

                <Button variant="ghost" size="sm" onClick={onExitEditMode}>
                  <X className="h-3.5 w-3.5 mr-1" />
                  Baigti
                </Button>
              </div>

              {showProfInput && totalSelectedCount > 0 && (
                <div className="flex items-center gap-3 pt-2 border-t">
                  <label className="text-sm font-medium">Lygis (0-100):</label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="5"
                    value={bulkProficiency}
                    onChange={(e) => onBulkProficiencyChange(e.target.value)}
                    className="w-32"
                    placeholder="pvz. 70"
                  />
                  <Button
                    size="sm"
                    onClick={onBulkSetProficiency}
                    disabled={isBulkUpdating || !bulkProficiency}
                  >
                    Pritaikyti ({totalSelectedCount})
                  </Button>
                  <Button variant="ghost" size="sm" onClick={onToggleProfInput}>
                    Atšaukti
                  </Button>
                </div>
              )}
            </div>
          )}

          {byGrade.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nėra įgūdžių šiame filtruotame rodinyje.
            </p>
          ) : (
            <div className="space-y-2">
              {byGrade.map(([grade, list]) => {
                const live = list.filter(isLive);
                const rest = list.filter((p) => !isLive(p));

                return (
                  <GradeGroup
                    // The filter is part of the identity, not just the props:
                    // with a bare grade key React reuses the instance and the
                    // group a grown-up just filtered down to stays folded shut.
                    key={`${grade}-${gradeFilter ?? "all"}`}
                    grade={grade}
                    proficiencies={list}
                    defaultOpen={shouldOpenGrade(grade, list)}
                  >
                    {live.length > 0 && (
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {live.map((prof) => (
                          <SkillCard
                            key={prof.id}
                            proficiency={prof}
                            isSelected={selectedIds.has(prof.id)}
                            onToggleSelect={onToggleSelect}
                            showCheckbox={showCheckboxes}
                            studentId={studentId}
                            onStatusChange={onStatusChange}
                            onTogglePriority={onTogglePriority}
                            busy={busy}
                          />
                        ))}
                      </div>
                    )}
                    {rest.map((prof) => (
                      <SkillRow
                        key={prof.id}
                        proficiency={prof}
                        isSelected={selectedIds.has(prof.id)}
                        onToggleSelect={onToggleSelect}
                        showCheckbox={showCheckboxes}
                        onStatusChange={onStatusChange}
                        onTogglePriority={onTogglePriority}
                        busy={busy}
                      />
                    ))}
                  </GradeGroup>
                );
              })}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// Lithuanian counts in three forms, and a prerequisite chain runs long enough
// to hit all of them: 1 žingsnis, 2-9 žingsniai, 10-20 and any round ten
// žingsnių.
function stepsLabel(count: number): string {
  const lastTwo = count % 100;
  const last = count % 10;

  if (lastTwo >= 11 && lastTwo <= 19) return `${count} žingsnių`;
  if (last === 1) return `${count} žingsnis`;
  if (last === 0) return `${count} žingsnių`;
  return `${count} žingsniai`;
}

// The same three forms, and the adjective has to move with the noun: 1 nebaigta
// sesija, 2-9 nebaigtos sesijos, 10-20 and any round ten nebaigtų sesijų.
function unfinishedSessionsPhrase(count: number): string {
  const lastTwo = count % 100;
  const last = count % 10;

  if (lastTwo >= 11 && lastTwo <= 19) return `${count} nebaigtų sesijų`;
  if (last === 1) return `${count} nebaigta sesija`;
  if (last === 0) return `${count} nebaigtų sesijų`;
  return `${count} nebaigtos sesijos`;
}

// What a grown-up is shown once a target is flagged: the route the sessions
// will actually take to it. Without this the app looks like it is ignoring the
// star — the target is usually locked and several skills away, so the next
// session is spent on something with a different name entirely.
function PriorityPlanCard({ plans }: { plans: PriorityPlan[] }) {
  if (plans.length === 0) return null;

  return (
    <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Star className="h-4 w-4 fill-amber-500 text-amber-500" />
          Prioritetai
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Sesijos skiria dalį užduočių keliui iki šių įgūdžių — pradedant nuo to,
          ką mokinys jau gali daryti.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {plans.map((plan) => {
          const reached = plan.steps.length === 0;
          const next = plan.steps[0];

          return (
            <div key={plan.targetSkillKey} className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <span>{plan.targetSkillName ?? plan.targetSkillKey}</span>
                {reached ? (
                  <Badge
                    variant="outline"
                    className="gap-1 border-emerald-300 text-emerald-700 dark:border-emerald-700 dark:text-emerald-300"
                  >
                    <CheckCircle2 className="h-3 w-3" />
                    Pasiekta
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs">
                    dar {stepsLabel(plan.steps.length)}
                  </Badge>
                )}
              </div>

              {!reached && (
                <>
                  <div className="flex items-center gap-1.5 flex-wrap text-xs">
                    {plan.steps.map((step, i) => (
                      <span key={step.skillKey} className="flex items-center gap-1.5">
                        {i > 0 && (
                          <ArrowRight className="h-3 w-3 text-muted-foreground/60" />
                        )}
                        <span
                          className={cn(
                            "rounded-md px-2 py-0.5",
                            step.skillKey === next?.skillKey
                              ? "bg-amber-500/20 font-medium text-amber-900 dark:text-amber-100"
                              : step.isUnlocked
                                ? "bg-muted"
                                : "bg-muted/50 text-muted-foreground"
                          )}
                        >
                          {step.skillName ?? step.skillKey}
                          {step.proficiency > 0 && (
                            <span className="ml-1 opacity-60">
                              {Math.round(step.proficiency)}%
                            </span>
                          )}
                        </span>
                      </span>
                    ))}
                  </div>
                  {next && (
                    <p className="text-xs text-muted-foreground">
                      Dabar dirbama su:{" "}
                      <span className="font-medium">
                        {next.skillName ?? next.skillKey}
                      </span>
                      {next.grade != null && ` (${gradeLabel(next.grade)})`}
                    </p>
                  )}
                </>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

/**
 * One line where four stat cards used to be.
 *
 * Of those four, "Viso įgūdžių" was the same number for every learner,
 * "Atrakinta" measured the shape of the prerequisite graph rather than the
 * child, and "Vidutinis lygis" averaged four hundred untouched zeros into a
 * number that read 21% for a learner sitting at 82% on everything she had
 * actually done.
 */
function SummaryBar({
  student,
  subjects,
  onOpenSetup,
}: {
  student: StudentSummary;
  subjects: Subject[];
  onOpenSetup: () => void;
}) {
  const all = subjects.flatMap((s) => s.proficiencies);
  const practised = all.filter((p) => p.totalAttempts > 0);
  const known = all.filter((p) => p.setupStatus === "known").length;
  const struggling = practised.filter((p) => p.proficiency < STRUGGLING_BELOW).length;
  const lastPractised = all
    .flatMap((p) => (p.history ?? []).map((h) => h.session?.completedAt ?? h.createdAt))
    .filter(Boolean)
    .sort()
    .at(-1);

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border bg-card px-4 py-3 text-sm">
      <span className="font-medium">{student.name}</span>
      {student.grade != null && (
        <span className={cn("text-muted-foreground", !student.gradeKnown && "italic")}>
          {gradeLabel(student.grade)}
          {!student.gradeKnown && " (pagal gimimo metus)"}
        </span>
      )}
      <span className="text-muted-foreground">·</span>
      <span className="text-muted-foreground">
        <span className="font-medium text-foreground">{known}</span> moka
      </span>
      <span className="text-muted-foreground">
        <span className="font-medium text-foreground">{practised.length}</span> spręsta
      </span>
      {struggling > 0 && (
        <span className="text-amber-700 dark:text-amber-400">
          <span className="font-medium">{struggling}</span> stringa
        </span>
      )}
      {lastPractised && (
        <span className="text-muted-foreground">
          paskutinį kartą {formatRelativeShort(lastPractised)}
        </span>
      )}
      <Button
        variant={student.skillSetupDone ? "ghost" : "default"}
        size="sm"
        onClick={onOpenSetup}
        className="ml-auto"
      >
        <Wand2 className="mr-1.5 h-3.5 w-3.5" />
        {student.skillSetupDone ? "Peržiūrėti planą" : "Nustatyti, ką jau moka"}
      </Button>
    </div>
  );
}

export function SkillsManager({
  student,
  subjects: rows,
  learnerSubjects,
  unfinishedSessions,
  priorityPlans,
  liveProficiencies,
  studentId,
}: SkillsManagerProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editMode, setEditMode] = useState(false);
  const [showProfInput, setShowProfInput] = useState(false);
  const [bulkProficiency, setBulkProficiency] = useState("");
  const [setupOpen, setSetupOpen] = useState(false);

  // A card's own detail arrives in its own list rather than on every row — see
  // `liveProficiencies` in GET_STUDENT_SKILLS — and is merged back onto its row
  // here, so everything below this line reads one object per skill. A row with
  // no card keeps its detail fields absent rather than zeroed.
  const subjects = useMemo(() => {
    const detailById = new Map(liveProficiencies.map((detail) => [detail.id, detail]));

    return rows.map((subject) => ({
      ...subject,
      proficiencies: subject.proficiencies.map((prof) => {
        const detail = detailById.get(prof.id);
        return detail ? { ...prof, ...detail } : prof;
      }),
    }));
  }, [rows, liveProficiencies]);

  const refetch = useMemo(
    () => [{ query: GET_STUDENT_SKILLS, variables: { id: studentId } }],
    [studentId]
  );

  // Read grade filters from URL search params (e.g., ?math=2&lithuanian=1)
  const getGradeFilter = useCallback(
    (subjectId: string): number | null => {
      const val = searchParams.get(subjectId);
      if (val === null) return null;
      const num = parseInt(val, 10);
      return isNaN(num) ? null : num;
    },
    [searchParams]
  );

  const setGradeFilter = useCallback(
    (subjectId: string, grade: number | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (grade === null) {
            next.delete(subjectId);
          } else {
            next.set(subjectId, String(grade));
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [bulkUpdateProficiencies, { loading: isBulkUpdating }] = useMutation(
    BULK_UPDATE_PROFICIENCIES,
    {
      refetchQueries: refetch,
      onCompleted: (data) => {
        if (data.bulkUpdateProficiencies.errors.length === 0) {
          toast.success(`Sėkmingai atnaujinta ${selectedIds.size} įgūdžių`);
          setSelectedIds(new Set());
          setEditMode(false);
          setShowProfInput(false);
          setBulkProficiency("");
        } else {
          toast.error(
            `Klaidos: ${data.bulkUpdateProficiencies.errors.join(", ")}`
          );
        }
      },
      onError: (error) => {
        toast.error(`Klaida: ${error.message}`);
      },
    }
  );

  const [applySkillSetup, { loading: isApplyingStatus }] = useMutation(APPLY_SKILL_SETUP, {
    refetchQueries: refetch,
    onError: (error) => toast.error(`Klaida: ${error.message}`),
  });

  const [setSubjectPin, { loading: isPinning }] = useMutation(SET_SUBJECT_PIN, {
    refetchQueries: refetch,
    onError: (error) => toast.error(`Klaida: ${error.message}`),
  });

  // Refetched rather than read off the reply: switching a strand off can open
  // skills that were waiting behind it, and those rows are in the skills query.
  const [setSubjectStrands, { loading: isSettingStrands }] = useMutation(SET_SUBJECT_STRANDS, {
    refetchQueries: refetch,
    onError: (error) => toast.error(`Klaida: ${error.message}`),
  });

  const [updateProficiency, { loading: isTogglingPriority }] = useMutation(
    UPDATE_PROFICIENCY,
    {
      refetchQueries: refetch,
      onError: (error) => {
        toast.error(`Klaida: ${error.message}`);
      },
    }
  );

  const busy = isApplyingStatus || isTogglingPriority || isPinning || isSettingStrands;

  const handleStatusChange = (proficiency: Proficiency, status: SkillStatus) => {
    const name = proficiency.skill?.name ?? proficiency.skillKey;

    applySkillSetup({
      variables: {
        studentId,
        statuses: [{ skillKey: proficiency.skillKey, status }],
      },
      onCompleted: (data) => {
        const errors: string[] = data.applySkillSetup.errors ?? [];
        if (errors.length > 0) {
          toast.error(errors.join(", "));
          return;
        }
        toast.success(`„${name}" — ${STATUS_META[status].label.toLowerCase()}`);
      },
    });
  };

  const handleBulkStatus = (status: SkillStatus) => {
    if (selectedIds.size === 0) return;

    const keys = subjects
      .flatMap((s) => s.proficiencies)
      .filter((p) => selectedIds.has(p.id))
      .map((p) => p.skillKey);

    applySkillSetup({
      variables: { studentId, statuses: keys.map((skillKey) => ({ skillKey, status })) },
      onCompleted: (data) => {
        const errors: string[] = data.applySkillSetup.errors ?? [];
        if (errors.length > 0) {
          toast.error(errors.join(", "));
          return;
        }
        toast.success(
          `${data.applySkillSetup.changedCount} įgūdžių — ${STATUS_META[status].label.toLowerCase()}`
        );
        setSelectedIds(new Set());
        setEditMode(false);
      },
    });
  };

  const handlePin = (subjectId: string, state: PinState) => {
    setSubjectPin({
      variables: { studentId, subjectId, state },
      onCompleted: (data) => {
        const errors: string[] = data.setSubjectPin.errors ?? [];
        if (errors.length > 0) toast.error(errors.join(", "));
      },
    });
  };

  const handleSetStrands = (subjectId: string, strands: string[]) => {
    setSubjectStrands({
      variables: { studentId, subjectId, strands },
      onCompleted: (data) => {
        const errors: string[] = data.setSubjectStrands.errors ?? [];
        if (errors.length > 0) toast.error(errors.join(", "));
      },
    });
  };

  const handleTogglePriority = (proficiency: Proficiency) => {
    const next = !proficiency.isPriority;
    const name = proficiency.skill?.name ?? proficiency.skillKey;

    updateProficiency({
      variables: { id: proficiency.id, isPriority: next },
      onCompleted: (data) => {
        if (data.updateProficiency.errors.length > 0) {
          toast.error(`Klaidos: ${data.updateProficiency.errors.join(", ")}`);
          return;
        }
        toast.success(
          next
            ? `„${name}" — prioritetas. Sesijos eis link jo.`
            : `„${name}" nebėra prioritetas.`
        );
      },
    });
  };

  const toggleSelect = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const selectGrade = (subjectId: string, grade: number) => {
    const subject = subjects.find((s) => s.id === subjectId);
    if (!subject) return;

    const gradeIds = studiedProficiencies(
      subject.proficiencies,
      subjectStanding.get(subjectId)?.strands
    )
      .filter((p) => p.skill?.grade === grade)
      .map((p) => p.id);

    const newSet = new Set(selectedIds);
    const allSelected = gradeIds.every((id) => newSet.has(id));

    if (allSelected) {
      gradeIds.forEach((id) => newSet.delete(id));
    } else {
      gradeIds.forEach((id) => newSet.add(id));
    }

    setSelectedIds(newSet);
  };

  const enterEditMode = () => setEditMode(true);

  const exitEditMode = () => {
    setEditMode(false);
    setSelectedIds(new Set());
    setShowProfInput(false);
    setBulkProficiency("");
  };

  const handleBulkSetPriority = (isPriority: boolean) => {
    if (selectedIds.size === 0) return;

    bulkUpdateProficiencies({
      variables: {
        updates: Array.from(selectedIds).map((id) => ({ id, isPriority })),
      },
    });
  };

  const handleBulkSetProficiency = () => {
    if (selectedIds.size === 0 || !bulkProficiency) return;

    const profValue = parseFloat(bulkProficiency);
    if (isNaN(profValue) || profValue < 0 || profValue > 100) {
      toast.error("Lygis turi būti tarp 0 ir 100");
      return;
    }

    bulkUpdateProficiencies({
      variables: {
        updates: Array.from(selectedIds).map((id) => ({
          id,
          proficiency: profValue,
          isUnlocked: profValue > 0 ? true : undefined,
        })),
      },
    });
  };

  // Which subjects get the full treatment: the ones this learner works in, plus
  // anything a grown-up pinned. Everything else folds into one strip.
  //
  // A learner nobody has set up yet works in nothing, and an empty page is no
  // use to the person who most needs this one — so they fall back to the
  // subjects that suit their grade, and only if even that is empty to all of
  // them. Falling back to *all six* would put the 501 rows this page exists to
  // remove in front of exactly the learner who cannot read them yet.
  const subjectStanding = useMemo(
    () => new Map(learnerSubjects.map((s) => [s.id, s])),
    [learnerSubjects]
  );
  const shownIds = useMemo(() => {
    const active = learnerSubjects.filter((s) => s.active);
    const recommended = learnerSubjects.filter((s) => s.recommended);
    const chosen = active.length > 0 ? active : recommended.length > 0 ? recommended : learnerSubjects;
    return new Set(chosen.map((s) => s.id));
  }, [learnerSubjects]);

  const activeSubjects = subjects.filter((s) => shownIds.has(s.id));
  const inactiveSubjects = learnerSubjects.filter(
    (s) => !shownIds.has(s.id) && subjects.some((subject) => subject.id === s.id)
  );
  const skillCounts = useMemo(
    () => Object.fromEntries(subjects.map((s) => [s.id, s.proficiencies.length])),
    [subjects]
  );

  return (
    <div className="space-y-6">
      <SummaryBar
        student={student}
        subjects={subjects}
        onOpenSetup={() => setSetupOpen(true)}
      />

      {setupOpen && (
        <SkillSetupWizard
          studentId={studentId}
          student={student}
          onClose={() => setSetupOpen(false)}
        />
      )}

      <PriorityPlanCard plans={priorityPlans} />

      {/* Unfinished sessions warning */}
      {unfinishedSessions.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20">
          <CardContent className="py-3">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                ⚠️ Yra {unfinishedSessionsPhrase(unfinishedSessions.length)}.
                Pažangos duomenys gali būti neaktualūs, kol jos nebaigtos.
              </p>
              <div className="flex items-center gap-2 shrink-0">
                {unfinishedSessions.length === 1 ? (
                  <Button size="sm" variant="outline" className="border-amber-300 dark:border-amber-700" asChild>
                    <Link to={`/sessions/${unfinishedSessions[0].id}`}>
                      <Play className="mr-1.5 h-3.5 w-3.5" />
                      Tęsti sesiją
                    </Link>
                  </Button>
                ) : (
                  unfinishedSessions.map((session) => (
                    <Button key={session.id} size="sm" variant="outline" className="border-amber-300 dark:border-amber-700" asChild>
                      <Link to={`/sessions/${session.id}`}>
                        <Play className="mr-1.5 h-3.5 w-3.5" />
                        Tęsti
                      </Link>
                    </Button>
                  ))
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {activeSubjects.map((subject) => (
        <SubjectSection
          key={subject.id}
          subject={{
            ...subject,
            proficiencies: studiedProficiencies(
              subject.proficiencies,
              subjectStanding.get(subject.id)?.strands
            ),
          }}
          learnerSubject={subjectStanding.get(subject.id)}
          learnerGrade={student.grade}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          showCheckboxes={editMode}
          onSelectGrade={selectGrade}
          editMode={editMode}
          onEnterEditMode={enterEditMode}
          onExitEditMode={exitEditMode}
          onBulkStatus={handleBulkStatus}
          onBulkSetPriority={handleBulkSetPriority}
          onStatusChange={handleStatusChange}
          onTogglePriority={handleTogglePriority}
          busy={busy}
          showProfInput={showProfInput}
          onToggleProfInput={() => setShowProfInput(!showProfInput)}
          bulkProficiency={bulkProficiency}
          onBulkProficiencyChange={setBulkProficiency}
          onBulkSetProficiency={handleBulkSetProficiency}
          isBulkUpdating={isBulkUpdating}
          totalSelectedCount={selectedIds.size}
          gradeFilter={getGradeFilter(subject.id)}
          onGradeFilterChange={(grade) => setGradeFilter(subject.id, grade)}
          onPin={handlePin}
          onSetStrands={handleSetStrands}
          studentId={studentId}
        />
      ))}

      <InactiveSubjectsStrip
        subjects={inactiveSubjects}
        skillCounts={skillCounts}
        onPin={handlePin}
        disabled={busy}
      />
    </div>
  );
}
